import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import Input from '../common/Input';
import Button from '../common/Button';
import LanguageSelector from '../common/LanguageSelector';
import { User, Mail, Lock, Shield } from 'lucide-react';

const SignUpForm: React.FC = () => {
  const { signUp, isLoading, error } = useAuth();
  const { labels, language } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return;
    }
    
    if (!privacyAgreed) {
      return;
    }
    
    await signUp(email, password, name.trim(), language);
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <Input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          required
          label={labels.displayName}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          leftIcon={<User size={18} />}
          fullWidth
        />
      </div>
      
      <div>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          label={labels.email}
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          leftIcon={<Mail size={18} />}
          fullWidth
        />
      </div>
      
      <div>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          label={labels.password}
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          leftIcon={<Lock size={18} />}
          fullWidth
        />
      </div>
      
      <div>
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          label={labels.confirmPassword}
          value={confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
          leftIcon={<Shield size={18} />}
          fullWidth
        />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-primary-700">
          {labels.language}
        </label>
        <LanguageSelector />
      </div>

      <div className="flex items-start space-x-3">
        <input
          id="privacy-agreement"
          name="privacy-agreement"
          type="checkbox"
          checked={privacyAgreed}
          onChange={(e) => setPrivacyAgreed(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-primary-300 rounded mt-1"
          required
        />
        <label htmlFor="privacy-agreement" className="text-sm text-primary-700">
          {labels.privacyAgreementText}{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-500 underline"
          >
            {labels.privacyAgreementLink}
          </a>
        </label>
      </div>

      {error && (
        <div className="bg-error-50 text-error-700 p-3 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <div>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          isFullWidth
          disabled={!privacyAgreed}
        >
          {labels.createAccount}
        </Button>
      </div>
    </form>
  );
};

export default SignUpForm; 